import { registerComponent, storeNumbersMatch } from "helpers";
import WYATTElement from "wyatt";

WYATT.storeDropdown = {
  markupCache: new Map(),
  inFlightRequests: new Map(),

  async requestMarkup(handle) {
    const response = await fetch(
      `${WYATT.routes.root}pages/store/${handle}?view=dropdown`,
    );

    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }

    return response.text();
  },

  async cachedRequest(handle) {
    try {
      const html = await this.requestMarkup(handle);

      this.markupCache.set(handle, html);

      return html;
    } finally {
      this.inFlightRequests.delete(handle);
    }
  },

  /**
   * The header renders a `<store-dropdown>` for both desktop and mobile, so both
   * instances ask for the same markup on every page. Sharing the pending request
   * keeps that to a single network call.
   * @param {string} handle
   * @returns {Promise<string>}
   */
  fetchMarkup(handle) {
    if (this.markupCache.has(handle)) {
      return Promise.resolve(this.markupCache.get(handle));
    }

    if (!this.inFlightRequests.has(handle)) {
      this.inFlightRequests.set(handle, this.cachedRequest(handle));
    }

    return this.inFlightRequests.get(handle);
  },
};

function findHandleFromStores(store) {
  if (!store?.store_number || !WYATT.shopify.stores?.length) {
    return null;
  }

  const match = WYATT.shopify.stores.find((s) =>
    storeNumbersMatch(s.store_number, store.store_number),
  );

  return match?.handle ?? null;
}

function resolveHandle(store) {
  if (store?.handle) return store.handle;

  return findHandleFromStores(store);
}

const FETCHED_DOM_ID = "default";

/**
 * Dropdown markup is fetched once with Liquid's default `dom_id`. Rewrite ids
 * (and matching `aria-controls`) so each custom element instance stays unique.
 * @param {Element} root
 * @param {string} instanceId
 */
function localizeFetchedIds(root, instanceId) {
  if (!root || !instanceId || instanceId === FETCHED_DOM_ID) {
    return;
  }

  const fromSuffix = `-${FETCHED_DOM_ID}`;
  const toSuffix = `-${instanceId}`;

  for (const element of root.querySelectorAll("[id]")) {
    if (!element.id.endsWith(fromSuffix)) {
      continue;
    }

    const previousId = element.id;
    const nextId = `${previousId.slice(0, -fromSuffix.length)}${toSuffix}`;

    element.id = nextId;

    for (const control of root.querySelectorAll(
      `[aria-controls="${previousId}"]`,
    )) {
      control.setAttribute("aria-controls", nextId);
    }
  }
}

registerComponent(
  "store-dropdown",
  class StoreDropdown extends WYATTElement {
    constructor() {
      super();

      this.pendingStore = null;
      this.fetchController = null;
      this.boundDelegatedClick = this.handleDelegatedClick.bind(this);
      this.onPreferredStoreChange = this.handlePreferredStoreChange.bind(this);
      this.onStoresFetched = this.handleStoresFetched.bind(this);
    }

    connectedCallback() {
      this.contentRoot = this.querySelector("[data-preferred-store-root]");
      this.emptyTemplate = this.querySelector(
        "template[data-preferred-store-empty]",
      );

      this.addEventListener("click", this.boundDelegatedClick);

      this.subscribe(
        WYATT.PubSub.events.preferredStoreChange,
        this.onPreferredStoreChange,
      );

      this.subscribe(WYATT.PubSub.events.storesFetched, this.onStoresFetched);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener("click", this.boundDelegatedClick);
      this.abortPendingFetch();
    }

    handleDelegatedClick(event) {
      if (event.target.closest("[data-close-menu]")) {
        this.querySelector("wyatt-menu")?.closeMenu();
      }
    }

    handleStoresFetched() {
      if (!this.pendingStore) return;

      const store = this.pendingStore;

      this.pendingStore = null;

      const handle = resolveHandle(store);

      if (handle) {
        this.loadAndMount(store, handle);
      } else {
        this.clearContentRoot();
      }
    }

    isAlreadyShowingStore(store) {
      if (!this.contentRoot?.querySelector("wyatt-menu") || !store) {
        return false;
      }

      return (
        store.store_number != null &&
        this.dataset.storeNumber &&
        storeNumbersMatch(store.store_number, this.dataset.storeNumber)
      );
    }

    handlePreferredStoreChange(store) {
      this.abortPendingFetch();

      if (!store || !store.name) {
        this.pendingStore = null;
        this.removeAttribute("data-store-number");
        this.showEmptyState();

        return;
      }

      if (this.isAlreadyShowingStore(store)) {
        return;
      }

      const handle = resolveHandle(store);

      if (!handle) {
        this.pendingStore = store;
        this.clearContentRoot();

        return;
      }

      this.pendingStore = null;
      this.loadAndMount(store, handle);
    }

    abortPendingFetch() {
      this.fetchController?.abort();
      this.fetchController = null;
    }

    clearContentRoot() {
      if (this.contentRoot) {
        this.contentRoot.innerHTML = "";
      }
    }

    showEmptyState() {
      if (!this.contentRoot) return;

      this.contentRoot.innerHTML = "";

      if (this.emptyTemplate?.content) {
        this.contentRoot.appendChild(
          this.emptyTemplate.content.cloneNode(true),
        );
      }
    }

    setStoreDataset(store) {
      if (store?.store_number != null) {
        this.dataset.storeNumber = String(store.store_number);
      }
    }

    async loadAndMount(store, handle) {
      const controller = new AbortController();

      this.fetchController = controller;

      this.clearContentRoot();

      try {
        const [html] = await Promise.all([
          WYATT.storeDropdown.fetchMarkup(handle),
          customElements.whenDefined("wyatt-menu"),
        ]);

        if (controller.signal.aborted) return;

        if (this.contentRoot) {
          this.contentRoot.innerHTML = html;
          localizeFetchedIds(this.contentRoot, this.config.id);
        }

        this.setStoreDataset(store);
      } catch {
        if (controller.signal.aborted) return;

        this.clearContentRoot();
      } finally {
        if (this.fetchController === controller) {
          this.fetchController = null;
        }
      }
    }
  },
);
